/**
 * Incident Log — chronological "what happened this quarter"
 * ===========================================================
 * Reads /api/incident-log/feed?month=YYYY-MM&category=
 * Lives at /admin/incident-log.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, ScrollText, MessageSquare, AlertTriangle, GitCompare,
  Calendar, Loader2, RefreshCw, ExternalLink,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  note:        { Icon: MessageSquare,   color: 'slate',   label: 'Annotation' },
  drift_alert: { Icon: AlertTriangle,   color: 'rose',    label: 'Drift alert' },
  rebalance:   { Icon: GitCompare,      color: 'emerald', label: 'Rebalance' },
};

const CATEGORIES = ['', 'compute', 'storage', 'egress', 'refunds', 'other'];

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const a = Math.abs(Number(n));
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(0)}K`;
  return `$${a.toFixed(0)}`;
};

const yyyymm = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const IncidentLog = () => {
  const today = new Date();
  const [month, setMonth] = useState(yyyymm(today));
  const [category, setCategory] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const fetchFeed = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { month };
      if (category) params.category = category;
      const { data } = await axios.get(`${API_URL}/incident-log/feed`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load feed (admin required).');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeed(); /* eslint-disable-next-line */ }, [month, category]);

  const monthOptions = useMemo(() => {
    const opts = [];
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    for (let i = 0; i < 12; i++) {
      opts.push(yyyymm(d));
      d.setMonth(d.getMonth() - 1);
    }
    return opts;
  }, []);  // eslint-disable-line

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead title="Incident Log · CARTIEAI" noindex />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/admin/cost-actuals" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="incident-log-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Cost Actuals
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · CFO narrative</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="incident-log-title">
            <ScrollText className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            Incident log
          </h1>
          <p className="text-sm sm:text-base text-slate-700 max-w-2xl">
            Chronological feed of every annotation, drift alert, and rebalance event.
            Filter by month + category. Print this for the quarterly business review.
          </p>
        </div>

        <Card className="border-emerald-500/20 mb-4">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Calendar className="w-4 h-4 text-slate-600" />
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-9 px-3 rounded-md border border-emerald-500/30 bg-slate-900 text-sm"
                data-testid="incident-log-month-select"
              >
                {monthOptions.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-9 px-3 rounded-md border border-emerald-500/30 bg-slate-900 text-sm"
                data-testid="incident-log-category-select"
              >
                <option value="">All categories</option>
                {CATEGORIES.filter(Boolean).map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <Button onClick={fetchFeed} variant="outline" size="sm" data-testid="incident-log-refresh">
                <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
              </Button>
              {data && (
                <span className="ml-auto text-xs text-slate-600 flex gap-2 flex-wrap" data-testid="incident-log-counts">
                  <Badge className="bg-slate-800 text-slate-100">total {data.count}</Badge>
                  {Object.entries(data.by_kind || {}).map(([k, v]) => {
                    const meta = KIND_META[k] || KIND_META.note;
                    return <Badge key={k} className={`bg-${meta.color}-100 text-${meta.color}-800`}>{meta.label}: {v}</Badge>;
                  })}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/10 mb-4">
            <CardContent className="pt-4 text-sm text-rose-200" data-testid="incident-log-error">{err}</CardContent>
          </Card>
        )}

        {loading && (
          <div className="text-sm text-slate-600 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}

        {data && !loading && data.events.length === 0 && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6 text-center py-12" data-testid="incident-log-empty">
              <ScrollText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <div className="text-sm text-slate-700 font-medium">No incidents recorded for {data.month}.</div>
              <div className="text-xs text-slate-600 mt-1">
                Add notes to spend points at <Link to="/admin/cost-actuals" className="text-emerald-300 underline">/admin/cost-actuals</Link> and they'll show up here.
              </div>
            </CardContent>
          </Card>
        )}

        {data && data.events.length > 0 && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-4">
              <ol className="space-y-3" data-testid="incident-log-events">
                {data.events.map((evt, i) => {
                  const meta = KIND_META[evt.kind] || KIND_META.note;
                  const Icon = meta.Icon;
                  const day = (evt.ts || '').split('T')[0];
                  return (
                    <li key={`${evt.ts}-${i}`} className="flex gap-3 pb-3 border-b border-slate-100 last:border-0" data-testid={`incident-log-event-${i}`}>
                      <div className={`shrink-0 w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 text-${meta.color}-700`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={`bg-${meta.color}-100 text-${meta.color}-800`}>{meta.label}</Badge>
                          <span className="text-xs text-slate-600">{day}</span>
                          {evt.category && <Badge className="bg-slate-800 text-slate-200">{evt.category}</Badge>}
                          {evt.provider && <Badge className="bg-emerald-100 text-blue-800">{evt.provider.toUpperCase()}</Badge>}
                        </div>
                        {evt.kind === 'note' && (
                          <div>
                            <div className="text-sm text-slate-900">{evt.note}</div>
                            <div className="text-xs text-slate-600 mt-0.5">
                              {fmtMoney(evt.amount_usd)} · source {evt.source}
                            </div>
                          </div>
                        )}
                        {evt.kind === 'drift_alert' && (
                          <div>
                            <div className="text-sm text-slate-900">
                              <b>{Math.abs(evt.drift_pp || 0).toFixed(1)}pp {evt.direction}</b> · {evt.severity} severity
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">Monthly spend: {fmtMoney(evt.monthly_spend_usd)}</div>
                          </div>
                        )}
                        {evt.kind === 'rebalance' && (
                          <div>
                            <div className="text-sm text-slate-900">
                              Coverage <b>{evt.from_coverage_pct}% → {evt.to_coverage_pct}%</b>
                              {' '}({evt.term?.toUpperCase()}) ·
                              {' '}<span className={evt.annual_savings_delta_usd >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                                {evt.annual_savings_delta_usd >= 0 ? '+' : ''}{fmtMoney(evt.annual_savings_delta_usd)}/yr
                              </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">
                              By {evt.actor_email}{evt.note ? ` · ${evt.note}` : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-500/20 bg-slate-900 text-slate-100 mt-4">
          <CardContent className="pt-4 text-xs text-slate-400">
            <ExternalLink className="w-3 h-3 inline mr-1" />
            Add narrative: visit <Link to="/admin/cost-actuals" className="text-emerald-300 hover:underline">/admin/cost-actuals</Link> and edit any row's note.
            High-drift Slack alerts and rebalance events auto-populate here.
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IncidentLog;
